import { INJECTED_APP_SETTINGS } from 'core/AppSettings.constants';
import { IAppSettings } from 'core/AppSettings.types';
import startsWith from 'lodash/fp/startsWith';
import urlJoin from 'url-join';

export type RequestHeaders = typeof REQUEST_HEADERS;

const REQUEST_HEADERS = {
  companyAlias: 'PEL-Company-Alias' as const,
  facilityAlias: 'PEL-Facility-Alias' as const,
};

// Cater for local development where Octopus doesn't run, so there's no substitution
const IS_LOCAL = startsWith('#{', INJECTED_APP_SETTINGS.cumulusApiUrl);

if (IS_LOCAL) {
  const PUBLIC_API_HOST = import.meta.env.VITE_PUBLIC_API_HOST || `http://localhost:60000/api`;
  const API_HOST = import.meta.env.VITE_API_HOST || `http://localhost:61000/api`;
  const CUMULUS_API_HOST = import.meta.env.VITE_CUMULUS_API_HOST || `https://localhost:7123`;
  const AUTH_HOST = import.meta.env.VITE_AUTH_HOST || `http://localhost:62000`;
  const REPORTING_HOST = import.meta.env.VITE_REPORTING_HOST || `https://localhost:5001/reports`;
  const GRAPHQL_HOST = import.meta.env.VITE_GRAPHQL_HOST || `http://localhost:60000/api/graphql`;
  const WS_HOST = import.meta.env.VITE_WS_HOST || `wss://localhost:5263`;
  const EXPORTS_HOST = import.meta.env.VITE_EXPORTS_HOST || `https://localhost:5002`;

  // Used for the returnUrl
  const OMNIS_HOST = import.meta.env.VITE_OMNIS_HOST || `http://localhost:3000`;
  // Only used for ES1 reports
  const ES1_HOST = import.meta.env.VITE_ES1_HOST || `http://localhost:60000`;

  INJECTED_APP_SETTINGS.apiKey = import.meta.env.VITE_API_KEY ?? '';
  INJECTED_APP_SETTINGS.apiUrl = API_HOST;
  INJECTED_APP_SETTINGS.authUrl = AUTH_HOST;
  INJECTED_APP_SETTINGS.beamerProductId = import.meta.env.VITE_BEAMER_PRODUCT_ID ?? '';
  INJECTED_APP_SETTINGS.bingMapsApiKey = import.meta.env.VITE_BING_MAPS_API_KEY ?? '';
  INJECTED_APP_SETTINGS.cumulusApiUrl = CUMULUS_API_HOST;
  INJECTED_APP_SETTINGS.debug = 'true';
  INJECTED_APP_SETTINGS.environment = import.meta.env.MODE ?? 'NO_MODE_INJECTED';
  INJECTED_APP_SETTINGS.es1Url = ES1_HOST;
  INJECTED_APP_SETTINGS.exportsUrl = EXPORTS_HOST;
  INJECTED_APP_SETTINGS.frontendVersion = crypto.getRandomValues(new Uint32Array(1))[0]?.toString() ?? '';
  INJECTED_APP_SETTINGS.googleAnalyticsMeasurementId = 'UA-8575676-11';
  INJECTED_APP_SETTINGS.googleMapsApiKey = 'AIzaSyAZsoqQGsDFID-waQbSinVR3NQkq9j-msQ';
  INJECTED_APP_SETTINGS.graphQLUrl = GRAPHQL_HOST;
  INJECTED_APP_SETTINGS.hasProject = 'true';
  INJECTED_APP_SETTINGS.icpCertificate = '';
  INJECTED_APP_SETTINGS.internetSecurityNumber = '';
  INJECTED_APP_SETTINGS.internetSecurityNumberText = '';
  INJECTED_APP_SETTINGS.isCommunity = import.meta.env.VITE_IS_COMMUNITY ?? 'false';
  INJECTED_APP_SETTINGS.mapboxApiKey =
    import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
    'pk.eyJ1IjoiZW52aXJvc3VpdGUtYnJpc2JhbmUiLCJhIjoiY2sybzY1OWNpMDd6ZDNkczd4Z2Z2cWxxZyJ9.-wuqJp_YE_zFr4JjPsZHAg';
  INJECTED_APP_SETTINGS.omnisCommunityAppHost =
    import.meta.env.VITE_OMNIS_COMMUNITY_APP_HOST || 'omnis-community.envirosuite.com';
  INJECTED_APP_SETTINGS.omnisUrl = OMNIS_HOST;
  INJECTED_APP_SETTINGS.publicApiUrl = PUBLIC_API_HOST;
  INJECTED_APP_SETTINGS.releaseNumber = 'Development';
  INJECTED_APP_SETTINGS.reportsServerUrl = REPORTING_HOST;
  INJECTED_APP_SETTINGS.wsUrl = WS_HOST;
}

if (!IS_LOCAL && !startsWith('http', INJECTED_APP_SETTINGS.cumulusApiUrl)) {
  throw new Error(
    `Cumulus API URL is not set correctly. Expected a full URL, got: ${INJECTED_APP_SETTINGS.cumulusApiUrl}`
  );
}

const createUrlBuilder =
  (urlBase: string) =>
  (...segments: string[]) =>
    urlJoin(urlBase, ...segments);

export const AppSettings: IAppSettings = {
  ...INJECTED_APP_SETTINGS,
  hasProject: INJECTED_APP_SETTINGS.hasProject.toLowerCase() === 'true',
  isDevelopment: import.meta.env.MODE === 'development',
  isProduction: !!INJECTED_APP_SETTINGS.omnisAppHost.match(/^omnis(-community)?.envirosuite.[a-z]+/),
  isCommunity: INJECTED_APP_SETTINGS.isCommunity.toLowerCase() === 'true',
  debug: INJECTED_APP_SETTINGS.debug.toLowerCase() === 'true',
  requestHeaders: REQUEST_HEADERS,
  buildApiUrl: createUrlBuilder(INJECTED_APP_SETTINGS.apiUrl),
  buildPublicApiUrl: createUrlBuilder(INJECTED_APP_SETTINGS.publicApiUrl),
};
